import React, { useState } from "react";

import { Table, Input, Space, Button } from "antd";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";
import ColorHash from "color-hash";

import { TimedCounts } from "../../types/api";
import { useAntdTheme } from "../../shared/hooks";

export type TimedCountsTableProps = {
  data: TimedCounts;
};

export const TimedCountsTable = (props: TimedCountsTableProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [searchText, setSearchText] = useState("");
  // Group by keys, then by timestamp.
  let timestamps: string[] = [];
  let rowMap: Map<string, any> = new Map<string, any>();
  Object.entries(props.data).forEach(([ts, counts]) => {
    timestamps.push(ts);
    Object.entries(counts).forEach(([key, v]) => {
      let row = rowMap.get(key);
      if (row === undefined) {
        row = {};
        rowMap.set(key, row);
      }
      row[ts] = v;
    });
  });

  let rows: any = [];
  for (let [key, m] of rowMap) {
    const row = { ...m };
    row["xxx_tr_row_key"] = key;
    rows.push(row);
  }

  const [filteredRows, setFilteredRows] = useState<any[]>(rows);

  // Update filteredRows when rows change (e.g., data prop updates)
  React.useEffect(() => {
    if (searchText) {
      const lowerCaseSearchText = searchText.toLowerCase();
      const filteredData = rows.filter((record: any) =>
        record["xxx_tr_row_key"].toLowerCase().includes(lowerCaseSearchText)
      );
      setFilteredRows(filteredData);
    } else {
      setFilteredRows(rows);
    }
  }, [JSON.stringify(rows), searchText]);

  const columns: any = [
    {
      title: "Name",
      dataIndex: "xxx_tr_row_key",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      sorter: (a: any, b: any) => {
        return a["xxx_tr_row_key"].localeCompare(b["xxx_tr_row_key"]);
      },
    },
  ];

  // Add a column for each timestamp.
  timestamps.sort();
  timestamps.forEach((ts: string) => {
    columns.push({
      // Backend returns timestamps already converted to local timezone
      title: dayjs(ts).format("MM/DD"),
      dataIndex: ts,
      defaultSortOrder: "descend",
      sortDirections: ["ascend", "descend"],
      sorter: (a: any, b: any) => {
        if (a[ts] < b[ts]) {
          return -1;
        } else if (a[ts] === b[ts]) {
          return 0;
        }
        return 1;
      },
    });
  });

  return (
    <div>
      <Input.Search
        placeholder={t("search_by_name")}
        allowClear
        enterButton
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onSearch={setSearchText}
        style={{ marginBottom: token.marginMD, width: 300, borderRadius: token.borderRadiusLG }}
      />
      <Table
        columns={columns}
        dataSource={filteredRows}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: "max-content" }}
        style={{ borderRadius: token.borderRadiusLG }}
      />
    </div>
  );
};
